import { useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Loader2, ShieldCheck } from "lucide-react";
import { useCustomerAuth } from "@/context/CustomerAuthContext";
import { useSecureMacProAccount } from "@/context/SecureMacProAccountContext";
import { fortressOwnership } from "@/lib/fortressOwnership";
import { HALO_WEBSITE_LIST_PRICE } from "@/config/haloStorefront";
import { FORTRESS_PRO_ADDON_PRICE } from "@/config/fortressStorefront";
import { useFortressStorefront } from "@/hooks/useFortressStorefront";
import {
  consumerErrorMessage,
  startFortressCheckout,
  startFortressProAddonCheckout,
} from "@/lib/consumerCommerce";

const PRO_PRICE_LINE = `Fortress Pro is ${FORTRESS_PRO_ADDON_PRICE.label} once with an active Halo SuperVPN subscription — ${FORTRESS_PRO_ADDON_PRICE.savingPercent}% less than SecureShield Pro at ${FORTRESS_PRO_ADDON_PRICE.standaloneLabel}.`;

export default function FortressAccountPanel() {
  const { customer, entitlements } = useCustomerAuth();
  const {
    account, accountError, entitlement, entitlementError, haloEntitlement, ready,
  } = useSecureMacProAccount();
  const { storefront, catalog } = useFortressStorefront();
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const ownership = fortressOwnership({
    customerId: customer?.id,
    websiteEntitlements: entitlements,
    consumerAccount: account,
    consumerEntitlement: entitlement,
    consumerHaloEntitlement: haloEntitlement,
    consumerReady: ready,
    consumerAccountError: accountError,
    consumerEntitlementError: entitlementError,
  });

  let proMessage;
  if (!ownership.proCheckAvailable) {
    proMessage = ready
      ? "We couldn't confirm your SecureShield Pro purchase right now. Please try again later."
      : "Checking your SecureShield Pro purchase…";
  } else if (ownership.proQualified) {
    proMessage = "You own Halo SuperVPN and SecureShield Pro, so this account qualifies for Fortress Pro.";
  } else if (ownership.haloPriceLinkPending) {
    proMessage = "Your Halo subscription is active. We're preparing your 40% saving on SecureShield Pro. Please check back here before buying.";
  } else if (ownership.halo) {
    proMessage = `Add Fortress Pro for ${FORTRESS_PRO_ADDON_PRICE.label} once with your active Halo SuperVPN subscription.`;
  } else if (ownership.secureMacPro) {
    proMessage = "Add a Halo SuperVPN subscription to your SecureShield Pro purchase for the Pro edition.";
  } else {
    proMessage = PRO_PRICE_LINE;
  }

  const go = async (key, start) => {
    setBusy(key);
    setError("");
    try {
      const { url } = await start();
      window.location.assign(url);
    } catch (err) {
      setError(err?.name === "ConsumerSaleClosedError" ? "This purchase isn't open yet." : consumerErrorMessage(err));
      setBusy("");
    }
  };
  // Buy buttons render only when the storefront is open (release approval AND
  // verified catalog). While sales are closed nothing here can start checkout.
  const baseBuy = storefront.base.purchasable && !ownership.baseQualified;
  const proBuy = storefront.pro.purchasable && ownership.proAddonEligible && haloEntitlement?.active === true;

  return (
    <section className="border border-ink-100 bg-white p-7" data-testid="fortress-account" aria-labelledby="fortress-account-heading">
      <div className="flex items-start gap-3">
        <ShieldCheck className="mt-1 h-6 w-6 shrink-0 text-electric" strokeWidth={1.6} />
        <div>
          <div className="lf-eyebrow">Two layers, one Mac app</div>
          <h2 id="fortress-account-heading" className="mt-2 font-display text-2xl font-bold">Fortress</h2>
          <p className="mt-2 text-sm leading-relaxed text-ink-600">Fortress is your Halo SuperVPN subscription and SecureShield in one Mac app. Fortress Pro adds SecureShield Pro for {FORTRESS_PRO_ADDON_PRICE.label} once.</p>
        </div>
      </div>
      <div className="mt-6 grid gap-3 sm:grid-cols-2">
        <div className="border border-ink-100 bg-ink-50/50 p-5" data-testid="fortress-base-status">
          <div className="text-xs font-semibold uppercase tracking-[.14em] text-ink-500">Halo subscription</div>
          <h3 className="mt-2 font-display text-xl font-bold">Fortress</h3>
          <p className="mt-2 text-sm leading-relaxed text-ink-600">
            {ownership.baseQualified
              ? "Your active Halo SuperVPN subscription covers Fortress on your Mac."
              : `Fortress is the Halo SuperVPN price: ${HALO_WEBSITE_LIST_PRICE.monthly}/month or ${HALO_WEBSITE_LIST_PRICE.annual}/year.`}
          </p>
          {baseBuy ? <div className="mt-4 flex flex-wrap gap-2" data-testid="fortress-base-buy">
            {[["monthly", `${HALO_WEBSITE_LIST_PRICE.monthly}/month`], ["annual", `${HALO_WEBSITE_LIST_PRICE.annual}/year`]].map(([plan, label]) => (
              <button key={plan} type="button" disabled={Boolean(busy)} onClick={() => go(plan, () => startFortressCheckout({ plan, catalog }))} className="lf-btn-primary h-10 px-4 text-[13px]" data-testid={`fortress-buy-${plan}`}>
                {busy === plan ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Buy {label}
              </button>
            ))}
          </div> : null}
          <Link className="lf-link mt-4 inline-flex items-center gap-2" to="/halo-fortress">Explore Fortress <ArrowRight className="h-4 w-4" /></Link>
        </div>
        <div className="border border-ink-100 bg-[#081a2d] p-5 text-white" data-testid="fortress-pro-status">
          <div className="text-xs font-semibold uppercase tracking-[.14em] text-cyan-200">{FORTRESS_PRO_ADDON_PRICE.label} once with Halo</div>
          <h3 className="mt-2 font-display text-xl font-bold">Fortress Pro</h3>
          <p className="mt-2 text-sm leading-relaxed text-slate-200">{proMessage}</p>
          {proBuy ? <button type="button" disabled={Boolean(busy)} onClick={() => go("pro", () => startFortressProAddonCheckout({ catalog, haloEntitlement }))} className="lf-btn mt-4 border border-cyan-300 bg-cyan-300 text-ink-900 hover:bg-white" data-testid="fortress-buy-pro">
            {busy === "pro" ? <Loader2 className="h-4 w-4 animate-spin" /> : null}Add Fortress Pro · {FORTRESS_PRO_ADDON_PRICE.label}
          </button> : null}
          <Link className="mt-4 flex items-center gap-2 text-sm font-semibold text-cyan-200 hover:text-white" to="/halo-fortress-pro">Explore Fortress Pro <ArrowRight className="h-4 w-4" /></Link>
        </div>
      </div>
      {error ? <p className="mt-4 text-sm text-red-700" role="alert" data-testid="fortress-account-error">{error}</p> : null}
      <p className="mt-4 text-xs text-ink-500">Prices in US dollars. Your existing apps stay in place until you choose to move and Fortress confirms your protection.</p>
    </section>
  );
}
